import { Controller } from "@hotwired/stimulus"
import { KokoroTTS } from "../lib/kokoro_tts"

// Conversation (voice) mode for the chat drawer. Browser-native Web Speech
// API — SpeechRecognition (speech→text) + speechSynthesis (text→speech).
//
// Speaks the reply as it STREAMS, sentence by sentence (the open-source
// JARVIS pattern): as the assistant's message grows token-by-token, each
// completed sentence is queued to TTS immediately instead of waiting for the
// whole answer. Picks a British male voice for the Jarvis timbre.
//
// State persists across the full-page reload a send triggers, so the
// hands-free loop survives: listen → transcribe → submit → (reload) →
// speak streaming reply → listen …
export default class extends Controller {
  static targets = [ "mic", "engine" ]
  static values  = { onKey: { type: String, default: "chat-voice-on" } }

  // Preferred voices, best first. Matched as case-insensitive name substrings.
  JARVIS_VOICES = [ "Daniel", "Arthur", "Google UK English Male", "Oliver",
                    "George", "Ryan", "Brian", "James" ]

  connect() {
    this.SR = window.SpeechRecognition || window.webkitSpeechRecognition
    this.supported = !!this.SR && !!window.speechSynthesis
    this._speaking = false
    this._listening = false
    this._queue = []
    this._streamId = null   // message id currently being voiced
    this._streamPos = 0      // chars of that message already queued

    if (!this.supported) { this._reflect(); return }

    this._loadVoice()
    window.speechSynthesis.onvoiceschanged = () => this._loadVoice()

    this._observeFeed()
    this._reflect()

    // Mic lives in the turbo-frame composer (reloads on switch/send) —
    // re-sync its visual state when the frame renders. A reply may already be
    // streaming into the freshly-rendered frame, so start the stream poll too.
    this._onFrameRender = (e) => {
      if (e.target && e.target.id === "drawer-frame") {
        this._reflect()
        if (this._enabled()) this._ensureStreamPoll()
      }
    }
    document.addEventListener("turbo:frame-render", this._onFrameRender)

    // Closing the drawer (X / Esc / backdrop / ⌘J) must stop the mic.
    this._onDrawerClose = () => this._stopForClose()
    document.addEventListener("drawer:close", this._onDrawerClose)

    if (this._enabled()) {
      KokoroTTS.preload()
      this._startEngineTicker()
      this._resumeAfterLoad()
    }
  }

  disconnect() {
    this._stopListening()
    this._stopSpeaking()
    this._stopEngineTicker()
    this._stopStreamPoll()
    if (this._observer) this._observer.disconnect()
    if (this._onFrameRender) document.removeEventListener("turbo:frame-render", this._onFrameRender)
    if (this._onDrawerClose) document.removeEventListener("drawer:close", this._onDrawerClose)
  }

  // Drawer closed → fully stop voice: end listening + speaking and turn the
  // mode off so nothing restarts in the background. Re-open + click the mic
  // to start again.
  _stopForClose() {
    if (!this._enabled() && !this._listening && !this._speaking) return
    this._setEnabled(false)
    this._stopListening()
    this._stopSpeaking()
    this._stopStreamPoll()
    this._reflect()
  }

  toggle(event) {
    if (event) event.preventDefault()
    if (!this.supported) {
      window.alert("Conversation mode needs a browser with the Web Speech API (Chrome, Edge, or Safari).")
      return
    }
    if (this._enabled()) {
      this._setEnabled(false)
      this._stopListening()
      this._stopSpeaking()
      this._stopEngineTicker()
    } else {
      this._setEnabled(true)
      KokoroTTS.preload()        // start downloading the Jarvis voice model
      this._startEngineTicker()
      this._startListening()
    }
    this._reflect()
  }

  // ── Voice selection ─────────────────────────────────────────────────
  _loadVoice() {
    const voices = window.speechSynthesis.getVoices() || []
    if (!voices.length) return
    const has = (v, s) => v.name.toLowerCase().includes(s.toLowerCase())
    const premium = (v) => has(v, "premium") || has(v, "enhanced") || has(v, "siri")

    // 1. Preferred British names, premium/enhanced variant first.
    for (const name of this.JARVIS_VOICES) {
      const named = voices.filter((v) => has(v, name))
      const best  = named.find(premium) || named[0]
      if (best) { this._chosenVoice = best; return }
    }
    // 2. Any premium en-GB voice, then any en-GB, then any English.
    this._chosenVoice =
      voices.find((v) => v.lang === "en-GB" && premium(v)) ||
      voices.find((v) => v.lang === "en-GB") ||
      voices.find((v) => v.lang && v.lang.startsWith("en")) ||
      voices[0]
  }

  // ── Speech → text ───────────────────────────────────────────────────
  _startListening() {
    if (this._listening || this._speaking || !this.supported) return
    const rec = new this.SR()
    rec.lang = "en-US"
    rec.interimResults = true
    rec.continuous = false
    rec.maxAlternatives = 1
    this._rec = rec
    this._listening = true
    this._reflect()

    let finalText = ""
    rec.onresult = (ev) => {
      let interim = ""
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i]
        if (r.isFinal) finalText += r[0].transcript
        else interim += r[0].transcript
      }
      const ta = this._textarea()
      if (ta) ta.value = (finalText + interim).replace(/\s+/g, " ").trim()
    }
    rec.onerror = () => { this._listening = false; this._reflect() }
    rec.onend = () => {
      this._listening = false
      this._reflect()
      const text = (this._textarea()?.value || "").trim()
      if (!this._enabled()) return
      if (text.length > 0) this._submit()
      else if (!this._speaking) setTimeout(() => this._startListening(), 500)
    }
    try { rec.start() } catch (_) { this._listening = false; this._reflect() }
  }

  _stopListening() {
    this._listening = false
    if (this._rec) { try { this._rec.abort() } catch (_) {} this._rec = null }
  }

  _submit() {
    const ta = this._textarea()
    const form = ta && ta.closest("form")
    if (!form) return
    sessionStorage.setItem("voice-expecting-reply", "1")
    this._stopListening()
    if (form.requestSubmit) form.requestSubmit()
    else form.submit()
  }

  // ── Streaming text → speech ─────────────────────────────────────────
  // Walks the latest assistant row; queues each newly-completed sentence as
  // it streams in. Flushes the tail + re-listens when the message completes.
  _pump() {
    if (!this._enabled()) return
    const row = this._latestAssistantRow()
    if (!row) return

    const id = parseInt(row.getAttribute("data-voice-id") || "0", 10)
    const lastDone = parseInt(sessionStorage.getItem("voice-last-spoken-id") || "0", 10)
    if (id <= lastDone) return

    if (this._streamId !== id) { this._streamId = id; this._streamPos = 0 }

    const text = this._rowText(row)
    // Drain every COMPLETE sentence available so far — one chunk each. The
    // first sentence becomes its own small chunk, so it synthesizes and starts
    // playing the instant it's done streaming, not after the whole answer.
    let guard = 0
    while (guard++ < 100) {
      const pending = text.slice(this._streamPos)
      const cut = this._firstSentenceCut(pending)
      if (cut <= 0) break
      const chunk = pending.slice(0, cut).trim()
      this._streamPos += cut
      if (chunk) this._enqueue(chunk)
    }

    if (row.getAttribute("data-voice-status") === "completed") {
      const tail = text.slice(this._streamPos).trim()
      if (tail) this._enqueue(tail)
      this._streamPos = text.length
      sessionStorage.setItem("voice-last-spoken-id", String(id))
      sessionStorage.removeItem("voice-expecting-reply")
      this._streamId = null
      this._stopStreamPoll()
    }
  }

  _enqueue(text) {
    this._queue.push(text)
    this._drain()
  }

  _drain() {
    if (this._speaking) return
    const next = this._queue.shift()
    if (!next) {
      // Queue empty — if the reply is fully voiced, resume listening.
      if (this._enabled() && this._streamId === null) this._relistenSoon()
      return
    }
    this._speaking = true
    this._reflect()
    this._speakChunk(next)
  }

  // Prefer the Kokoro "Lewis" voice; fall back to the browser voice while the
  // model is still downloading or if it errors.
  async _speakChunk(text) {
    const done = () => { this._speaking = false; this._reflect(); this._drain() }

    if (KokoroTTS.ready() && !KokoroTTS.failed()) {
      try {
        const blob = await KokoroTTS.synthesize(text)
        if (!this._enabled()) { this._speaking = false; return } // toggled off mid-synthesis
        const audio = new Audio(URL.createObjectURL(blob))
        this._audio = audio
        const finish = () => { try { URL.revokeObjectURL(audio.src) } catch (_) {} done() }
        audio.onended = finish
        audio.onerror = finish
        await audio.play()
        return
      } catch (_) {
        // fall through to browser TTS
      }
    }

    this._speakBrowser(text, done)
  }

  _speakBrowser(text, done) {
    const u = new SpeechSynthesisUtterance(text)
    if (this._chosenVoice) u.voice = this._chosenVoice
    u.lang = this._chosenVoice?.lang || "en-GB"
    u.rate = 1.18
    u.pitch = 1.0
    u.onend = done
    u.onerror = done
    window.speechSynthesis.speak(u)
  }

  _stopSpeaking() {
    this._queue = []
    if (window.speechSynthesis) window.speechSynthesis.cancel()
    if (this._audio) { try { this._audio.pause() } catch (_) {} this._audio = null }
    this._speaking = false
  }

  _relistenSoon() {
    if (this._listening || this._speaking) return
    setTimeout(() => { if (!this._listening && !this._speaking) this._startListening() }, 300)
  }

  // Index just past the FIRST complete sentence in `s` (0 if none yet). If the
  // buffer grows long with no sentence punctuation, break at a space so a
  // run-on doesn't stall playback.
  _firstSentenceCut(s) {
    const m = /[.!?…](?=\s|$)/.exec(s)
    if (m) return m.index + 1
    if (s.length > 200) {
      const sp = s.lastIndexOf(" ", 200)
      return sp > 0 ? sp + 1 : 0
    }
    return 0
  }

  // ── Feed watching ──────────────────────────────────────────────────
  _observeFeed() {
    const target = document.getElementById("jarvis-drawer-root") || document.body
    this._observer = new MutationObserver(() => this._schedulePump())
    this._observer.observe(target, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: [ "data-voice-status" ]
    })
  }

  // Coalesce the storm of stream mutations into one pump per frame.
  _schedulePump() {
    this._ensureStreamPoll()
    if (this._pumpScheduled) return
    this._pumpScheduled = true
    requestAnimationFrame(() => { this._pumpScheduled = false; this._pump() })
  }

  // Safety net: while a reply is streaming, drive _pump on a fixed interval so
  // we extract + speak each sentence the moment it completes — even if the
  // MutationObserver coalesces or misses a streamed update. _pump is
  // idempotent (it only ever queues text past _streamPos), so this can't
  // double-speak. Self-stops once no assistant row is still "running".
  _ensureStreamPoll() {
    if (this._streamPoll || !this._enabled()) return
    this._streamPoll = setInterval(() => {
      this._pump()
      const row = this._latestAssistantRow()
      const running = row && row.getAttribute("data-voice-status") === "running"
      if (!running || !this._enabled()) this._stopStreamPoll()
    }, 250)
  }

  _stopStreamPoll() {
    if (this._streamPoll) { clearInterval(this._streamPoll); this._streamPoll = null }
  }

  _resumeAfterLoad() {
    if (sessionStorage.getItem("voice-expecting-reply") === "1") {
      this._pump() // reply may already be streaming/complete
    } else {
      this._primeLastSpoken() // don't read old history aloud
      setTimeout(() => this._startListening(), 400)
    }
  }

  _latestAssistantRow() {
    const rows = document.querySelectorAll("#drawer-feed [data-voice-role='assistant'][data-voice-id]")
    return rows.length ? rows[rows.length - 1] : null
  }

  _primeLastSpoken() {
    let max = 0
    document.querySelectorAll("#drawer-feed [data-voice-role='assistant'][data-voice-id]").forEach((r) => {
      const id = parseInt(r.getAttribute("data-voice-id") || "0", 10)
      if (id > max) max = id
    })
    sessionStorage.setItem("voice-last-spoken-id", String(max))
  }

  // Only the rendered reply body (.prose) is ever voiced. Before streaming
  // starts the row holds just chrome — name, role, timestamp, "typing…" — and
  // falling back to row.textContent would speak all of that aloud.
  _rowText(row) {
    const prose = row.querySelector(".prose")
    if (!prose) return ""
    return (prose.textContent || "").replace(/\s+/g, " ").trim()
  }

  // ── helpers ────────────────────────────────────────────────────────
  _textarea() { return document.getElementById("drawer-textarea") }
  _enabled()  { return localStorage.getItem(this.onKeyValue) === "1" }
  _setEnabled(v) { localStorage.setItem(this.onKeyValue, v ? "1" : "0") }

  // ── Active-voice indicator ─────────────────────────────────────────
  _startEngineTicker() {
    if (this._engineTimer) return
    this._engineTimer = setInterval(() => {
      this._updateEngineLabel()
      const st = KokoroTTS.status()
      if (!this._enabled() || st === "ready" || st === "failed") this._stopEngineTicker()
    }, 600)
  }

  _stopEngineTicker() {
    if (this._engineTimer) { clearInterval(this._engineTimer); this._engineTimer = null }
  }

  _updateEngineLabel() {
    if (!this.hasEngineTarget) return
    const el = this.engineTarget
    if (!this._enabled() || !this.supported) { el.textContent = ""; el.title = ""; return }

    switch (KokoroTTS.status()) {
      case "ready":
        el.textContent = "Lewis"
        el.title = "Neural voice (Kokoro · Lewis)"
        break
      case "loading": {
        const p = KokoroTTS.progress()
        if (p > 0) {
          el.textContent = `Loading Lewis… ${p}%`
        } else {
          // The CDN omits content-length, so there's no %. Show elapsed
          // seconds instead so a large one-time download doesn't look frozen.
          if (!this._loadStartedAt) this._loadStartedAt = Date.now()
          const secs = Math.floor((Date.now() - this._loadStartedAt) / 1000)
          el.textContent = `Loading Lewis… ${secs}s`
        }
        el.title = "Downloading the neural voice model (~80 MB, one-time, then cached)"
        break
      }
      case "failed":
        el.textContent = "Browser voice"
        el.title = "WebGPU/model unavailable — using the browser's built-in voice"
        break
      default:
        el.textContent = ""
        el.title = ""
    }
  }

  _reflect() {
    this._updateEngineLabel()
    if (!this.hasMicTarget) return
    const state = !this.supported ? "unsupported"
      : this._speaking ? "speaking"
      : this._listening ? "listening"
      : this._enabled() ? "on" : "off"

    this.micTargets.forEach((btn) => {
      btn.dataset.voiceState = state
      btn.classList.remove("bg-violet-600", "bg-emerald-600", "text-white", "animate-pulse",
                            "text-ink-muted", "opacity-40")
      switch (state) {
        case "listening":   btn.classList.add("bg-violet-600", "text-white", "animate-pulse"); break
        case "speaking":    btn.classList.add("bg-emerald-600", "text-white"); break
        case "on":          btn.classList.add("bg-violet-600", "text-white"); break
        case "unsupported": btn.classList.add("text-ink-muted", "opacity-40"); break
        default:            btn.classList.add("text-ink-muted")
      }
      btn.title = {
        listening:   "Listening… (click to stop conversation mode)",
        speaking:    "Speaking…",
        on:          "Conversation mode on (click to stop)",
        unsupported: "Voice not supported in this browser",
        off:         "Start conversation mode (voice)"
      }[state]
    })
  }
}
